<script setup lang="ts">
  import SideBar from '@/components/SideBar.vue'
  import { type MenuItem, menuItems } from '@/routes'
  import { useCustomerStore } from '@/store/customer.store'
  import { usePortalCustomizationStore } from '@/store/portal-customization.store'
  import { useUserStore } from '@/store/user.store'
  import { parseJwt } from '@/utils/jwt.utils'
  import { applyPortalTheme, clearPortalTheme } from '@/utils/portal-theme'
  import { require, ToastMessage } from '@logistiek-zonder-papier/lzp-library'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const userStore = useUserStore()
  const customerStore = useCustomerStore()
  const portalCustomizationStore = usePortalCustomizationStore()

  const route = useRoute()
  const router = useRouter()

  const actualMenu = ref<MenuItem[]>([])

  function calculateMenu(modules: number[]) {
    const filteredMenu = menuItems.filter(module => {
      const hasModule = module.module == null || modules.includes(module.module)
      const hasRole =
        module.roles == null ||
        module.roles.length === 0 ||
        (userStore.currentNestUser && module.roles.includes(userStore.currentNestUser.role))

      return hasModule && hasRole
    })
    for (const menuItem of filteredMenu) {
      menuItem.subItems = menuItem.subItems?.filter(module => {
        const hasModule = module.module == null || modules.includes(module.module)
        const hasRole =
          module.roles == null ||
          module.roles.length === 0 ||
          (userStore.currentNestUser && module.roles.includes(userStore.currentNestUser.role))

        return hasModule && hasRole
      })
    }

    return filteredMenu.filter(
      menuItem => (menuItem.subItems?.length ?? 0) > 0 || menuItem.url != null
    )
  }

  watch(
    [() => customerStore.currentCustomer, () => userStore.currentNestUser],
    () => {
      if (customerStore.currentCustomer == null || userStore.currentNestUser == null) {
        return
      }

      actualMenu.value = calculateMenu(customerStore.currentCustomer.modules)

      const firstMenuItem = require(actualMenu.value[0], 'No menu items are available.')
      if (route.fullPath === '/' || route.fullPath === '/home') {
        const url = firstMenuItem.url ?? firstMenuItem.subItems?.[0]?.url
        router.push(require(url))
      }
    },
    { immediate: true }
  )

  onMounted(async () => {
    const token = localStorage.getItem('jwt_token')
    if (route.fullPath === '/' && token && parseJwt(token)?.['environment'] != 'nestjs') {
      await router.replace('/home')
      return
    }
    await userStore.fetchMe()
    await customerStore.fetchCurrentCustomer()
    if (!customerStore.currentCustomer) return

    await Promise.all([
      portalCustomizationStore.fetchPortalCustomization(customerStore.currentCustomer.id),
      portalCustomizationStore.fetchLogo(customerStore.currentCustomer?.imageFileId),
    ])
  })

  watch(() => portalCustomizationStore.activeColors, applyPortalTheme, { deep: true })

  onUnmounted(() => {
    clearPortalTheme()
  })
</script>

<template>
  <div class="layout">
    <SideBar
      v-if="userStore.currentNestUser && customerStore.currentCustomer"
      :toggled="true"
      :user="userStore.currentNestUser"
      :customer="customerStore.currentCustomer"
      :menu="actualMenu"
    />

    <div class="content">
      <RouterView />
    </div>
  </div>

  <ToastMessage />
</template>

<style scoped>
  .layout {
    display: flex;
    flex-direction: row;
    background-color: var(--colours-grey-2);
    width: 100%;
    height: 100dvh;
  }

  .sidebar {
    border-right: 1px solid
      color-mix(in srgb, var(--colours-sidebar-text, var(--colours-grey-5)) 15%, transparent);
    max-width: 256px;
    min-width: 256px;
  }

  .content {
    min-height: 100dvh;
    width: 100%;
    overflow: auto;
  }
</style>
